import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { cancelRazorpayPayment, createRazorpayOrder, verifyRazorpayPayment } from '../services/razorpayPayments';
import { ensureGuestInvoice, listGuestInvoices } from '../api/invoices';
import {
  addBookingAddons,
  applyBookingCardOffer,
  applyBookingCoupon,
  confirmBooking,
  createDraftBooking,
  getBookingById,
  saveGuestDetails,
} from '../api/booking';
import useCustomerAuth from '../hooks/useCustomerAuth';
import { useBookingContext } from '../context/BookingContext';
import { useStoreSelector } from '../hooks/useStoreSelector';
import useBookingStore, {
  selectAppliedCoupon,
  selectBookingId,
  selectCardOfferId,
  selectCheckIn,
  selectCheckOut,
  selectGuestDetails,
  selectGuests,
  selectPaymentOptionSelected,
  selectSelectedAddons,
  selectSetBookingId,
  selectSetPaymentOptionSelected,
  selectStayData,
  selectStayId,
} from '../store/bookingStore';
import { normalizeBookingDraft } from '../utils/bookingMapper';
import { requireBookingId, requireConfirmed } from '../utils/bookingGuards';
import { formatDateRange, formatGuests, formatInr } from '../utils/bookingUtils';
import { getTaxFeeLineItems } from '../utils/taxFeeBreakdown';
import { normalizeBookingPricing } from '../../utils/bookingPricing';
import { loadRazorpayCheckout } from '../utils/loadRazorpayCheckout';
import styles from './PaymentPage.module.css';
import '../../mobile/styles/mobile-theme.css';


const MapPinIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 11.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.5 8.5c0 4.5-5.5 8-5.5 8S4.5 13 4.5 8.5a5.5 5.5 0 1 1 11 0Z"
    />
  </svg>
);

const CalendarIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <rect x="3.5" y="4.5" width="13" height="12" rx="2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 3.5v3M14 3.5v3M3.5 8.5h13" />
  </svg>
);

const UsersIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 8.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 9.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.5 16.5a4 4 0 0 1 8 0M11 16.5a3.5 3.5 0 0 1 6 0"
    />
  </svg>
);

const ShieldCheckIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 3 4.5 5v4.5c0 3.8 2.6 6.7 5.5 7.5 2.9-.8 5.5-3.7 5.5-7.5V5L10 3Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="m7.5 10 1.8 1.8L12.5 8.5" />
  </svg>
);

const LockIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <rect x="4" y="9" width="12" height="8" rx="2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 9V6a3 3 0 1 1 6 0v3" />
  </svg>
);

const WalletIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" {...props}>
    <rect x="2.5" y="5" width="15" height="10" rx="2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12.5 10h4" />
  </svg>
);

const PAYMENT_METHODS = [
  {
    id: 'razorpay',
    label: 'Razorpay',
    description: 'Open Razorpay checkout and choose UPI, card, wallet, or netbanking there.',
    icon: WalletIcon,
    recommended: true,
    recommendedBanner: true,
    highlight: true,
  },
];

const PAYMENT_CANCELLED_MESSAGE =
  'Payment was not completed. No amount was charged. You can retry the payment when you are ready.';

const PAYMENT_PROVIDER_HINTS = {
  razorpay: 'Powered by Razorpay',
};

const PAYMENT_METHOD_GROUPS = [
  {
    id: 'gateway',
    tabLabel: 'Razorpay',
    title: 'Razorpay payment gateway',
    description: 'All final payments are processed only through Razorpay.',
    badge: 'Most used',
    methods: ['razorpay'],
  },
];

const PAYMENT_METHOD_TO_GROUP = PAYMENT_METHOD_GROUPS.reduce((acc, group) => {
  group.methods.forEach((methodId) => {
    acc[methodId] = group.id;
  });
  return acc;
}, {});

const RAZORPAY_CHECKOUT_CONFIG = {
  display: {
    blocks: {
      upiQr: {
        name: 'Scan UPI QR',
        instruments: [
          {
            method: 'upi',
            flows: ['qr'],
          },
        ],
      },
    },
    sequence: ['block.upiQr', 'upi', 'card', 'netbanking', 'wallet'],
    preferences: {
      show_default_blocks: true,
    },
  },
};

const PaymentActionBar = ({ payLabel, onPay, onBack, disabled, termsAccepted, onTermsChange, termsError }) => (
  <div className={styles.actionCard}>
    <div className={styles.actionSurface}>
      <div className={styles.termsCard}>
        <label className={styles.termsLabel}>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={onTermsChange}
            className={`${styles.checkControl} mt-0.5 h-4 w-4 rounded border-[#DDD5C7] text-[#0F5C5A] focus:ring-[#DDF1EE]`}
          />
          <span>
            I agree to the{' '}
            <Link to="/terms" className="font-semibold text-[#8A6428] hover:text-[#B78A3E]">
              Terms & Conditions
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="font-semibold text-[#8A6428] hover:text-[#B78A3E]">
              Privacy Policy
            </Link>
            .
          </span>
        </label>
        {termsError && <p className={styles.termsError}>{termsError}</p>}
      </div>
      <div className={styles.actionButtons}>
        <button
          type="button"
          onClick={onPay}
          disabled={disabled}
          className={`${styles.primaryAction} disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {payLabel}
        </button>
        <button
          type="button"
          onClick={onBack}
          className={styles.secondaryAction}
        >
          Back to review
        </button>
      </div>
    </div>
  </div>
);

const PaymentCancelledPanel = ({
  bookingId,
  payableNow,
  total,
  remainingAmount,
  currency,
  isPartial,
  paymentMode,
  onRetry,
  onReview,
  disabled,
  supportLine,
}) => (
  <section className={styles.cancelPanel} role="status" aria-live="polite">
    <div className={styles.cancelIcon} aria-hidden="true">!</div>
    <div className={styles.cancelContent}>
      <p className={styles.cancelEyebrow}>Payment paused</p>
      <h2 className={styles.cancelTitle}>No amount was charged</h2>
      <p className={styles.cancelText}>
        Razorpay checkout was closed before payment completion. Your booking is still saved, so you can retry securely
        whenever you are ready.
      </p>
      <div className={styles.cancelSnapshot}>
        <div>
          <span>Booking</span>
          <strong>{bookingId ? `#${bookingId}` : 'Draft booking'}</strong>
        </div>
        <div>
          <span>Payment mode</span>
          <strong>{isPartial ? `Partial${paymentMode ? ` (${paymentMode})` : ''}` : 'Full payment'}</strong>
        </div>
        <div>
          <span>Pay now</span>
          <strong>{formatInr(payableNow, currency)}</strong>
        </div>
        {isPartial ? (
          <div>
            <span>Pay at check-in</span>
            <strong>{formatInr(remainingAmount, currency)}</strong>
          </div>
        ) : (
          <div>
            <span>Total payable</span>
            <strong>{formatInr(total, currency)}</strong>
          </div>
        )}
      </div>
      <div className={styles.cancelActions}>
        <button type="button" onClick={onRetry} disabled={disabled} className={styles.cancelPrimary}>
          Retry secure payment
        </button>
        <button type="button" onClick={onReview} className={styles.cancelSecondary}>
          Review booking
        </button>
      </div>
      <p className={styles.cancelSupport}>{supportLine}</p>
    </div>
  </section>
);

const PaymentMethodTab = ({ method, isActive, onSelect }) => {
  const Icon = method.icon;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isActive}
      className={`${styles.methodOptionTab} ${
        isActive ? styles.methodOptionTabActive : ''
      } ${method.highlight && isActive ? styles.methodOptionTabHighlight : ''}`}
    >
      <span className={`${styles.methodIcon} ${isActive ? styles.methodIconActive : ''} ${method.highlight && isActive ? styles.methodIconHighlight : ''}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className={styles.methodOptionTabLabel}>{method.label}</span>
      {method.recommended ? (
        <span className={`${styles.methodOptionTag} ${method.highlight && isActive ? styles.methodOptionTagHighlight : ''}`}>
          Most used
        </span>
      ) : null}
    </button>
  );
};

const PaymentDetailsPanel = ({
  methodId,
  isPartialPaymentSelected,
  partialPaymentPercentage,
  payableNow,
  remainingAmount,
  pricingCurrency,
}) => {
  const method = PAYMENT_METHODS.find((item) => item.id === methodId) || PAYMENT_METHODS[0];
  const providerHint = PAYMENT_PROVIDER_HINTS[methodId] || 'Powered by Homevilas';

  return (
    <div className={styles.detailsPanel}>
      <div className={styles.detailsHeader}>
        <div>
          <h3 className={styles.detailsTitle}>Secure checkout</h3>
          <p className={styles.detailsDescription}>Continue to the secure payment window to complete your booking.</p>
          <p className={styles.detailsProvider}>{providerHint}</p>
        </div>
        <span className={styles.detailsMethodTag}>{method.label}</span>
      </div>

      {isPartialPaymentSelected ? (
        <div className={styles.paymentPlanCard}>
          <div className={styles.paymentPlanHeader}>
            <span className={styles.paymentPlanBadge}>Partial payment</span>
            <span className={styles.paymentPlanPercent}>{partialPaymentPercentage}% now</span>
          </div>
          <div className={styles.paymentPlanRow}>
            <span>Amount to pay now</span>
            <strong>{formatInr(payableNow, pricingCurrency)}</strong>
          </div>
          <div className={styles.paymentPlanRow}>
            <span>Pay at check-in</span>
            <strong>{formatInr(remainingAmount, pricingCurrency)}</strong>
          </div>
        </div>
      ) : null}

      <div className="mt-4 space-y-4">
        <div className="space-y-3">
          <div className={styles.detailsNote}>
            You will be redirected to Razorpay checkout to complete your payment securely.
          </div>
          <p className={styles.detailsHint}>Supports cards, UPI, wallets, and netbanking.</p>
        </div>
      </div>
    </div>
  );
};

const PaymentPage = () => {
  const { bookingId: bookingIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { accessToken } = useCustomerAuth();
  const { bookingId: contextBookingId, draft, setBookingId, setDraft } = useBookingContext();
  const stayId = useStoreSelector(useBookingStore, selectStayId);
  const stayData = useStoreSelector(useBookingStore, selectStayData);
  const checkIn = useStoreSelector(useBookingStore, selectCheckIn);
  const checkOut = useStoreSelector(useBookingStore, selectCheckOut);
  const guests = useStoreSelector(useBookingStore, selectGuests);
  const selectedAddons = useStoreSelector(useBookingStore, selectSelectedAddons);
  const paymentOptionSelected = useStoreSelector(useBookingStore, selectPaymentOptionSelected);
  const cardOfferId = useStoreSelector(useBookingStore, selectCardOfferId);
  const appliedCoupon = useStoreSelector(useBookingStore, selectAppliedCoupon);
  const guestDetails = useStoreSelector(useBookingStore, selectGuestDetails);
  const storeBookingId = useStoreSelector(useBookingStore, selectBookingId);
  const setStoreBookingId = useStoreSelector(useBookingStore, selectSetBookingId);
  const setPaymentOptionSelected = useStoreSelector(useBookingStore, selectSetPaymentOptionSelected);
  const [pageError, setPageError] = useState('');
  const [actionError, setActionError] = useState('');
  const [paymentOptionUpdating, setPaymentOptionUpdating] = useState(false);
  const [razorpayLoading, setRazorpayLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('razorpay');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [billingSameAsGuest, setBillingSameAsGuest] = useState(true);
  const [billingFields, setBillingFields] = useState(() => ({
    name: guestDetails?.fullName || '',
    email: guestDetails?.email || '',
    phone: guestDetails?.phone || '',
    address: '',
  }));
  const [formErrors, setFormErrors] = useState({});
  const rehydrateAttemptRef = useRef(false);
  const queryBookingId = String(searchParams.get('bookingId') || '').trim();

  const navigateToInvoice = useCallback(
    async (bookingId) => {
      try {
        const invoices = await listGuestInvoices({ bookingId, limit: 1 });
        let invoice = invoices?.data?.[0];
        if (!invoice?.id) {
          invoice = await ensureGuestInvoice(bookingId);
        }
        if (invoice?.id) {
          navigate(`/guest/invoices/${invoice.id}`, { replace: true });
          return true;
        }
      } catch (err) {
        console.warn('Invoice redirect failed', err);
      }
      return false;
    },
    [navigate]
  );

  const effectiveBookingId = bookingIdParam || queryBookingId || contextBookingId || draft?.id || storeBookingId || '';
  const selectionDraft = useMemo(() => {
    if (!stayId || !checkIn || !checkOut) return null;
    return {
      id: storeBookingId,
      stayId,
      stayName: stayData?.title || stayData?.name || '',
      location: [stayData?.city, stayData?.area].filter(Boolean).join(', '),
      checkIn,
      checkOut,
      guests,
      cardOfferId,
      paymentOptionSelected,
      addons: selectedAddons || [],
      coupon: appliedCoupon || null,
      guestDetails: guestDetails || null,
    };
  }, [appliedCoupon, checkIn, checkOut, guests, guestDetails, selectedAddons, stayData, stayId, storeBookingId, cardOfferId, paymentOptionSelected]);
  const fallbackDraft = useMemo(() => {
    if (draft) return draft;
    return selectionDraft || null;
  }, [draft, selectionDraft]);
  const [booking, setBooking] = useState(fallbackDraft);
  const [loading, setLoading] = useState(!fallbackDraft);

  useEffect(() => {
    if (!requireBookingId(effectiveBookingId, navigate)) return;
  }, [effectiveBookingId, navigate]);

  useEffect(() => {
    if (fallbackDraft) {
      setBooking((prev) => {
        if (prev?.id && String(prev.id) === String(fallbackDraft.id)) return prev;
        return fallbackDraft;
      });
      if (fallbackDraft?.id && !contextBookingId && !bookingIdParam) {
        setBookingId(fallbackDraft.id);
      }
      if (fallbackDraft?.id && setStoreBookingId) {
        setStoreBookingId(fallbackDraft.id);
      }
    }
  }, [bookingIdParam, contextBookingId, fallbackDraft, queryBookingId, setBookingId, setStoreBookingId]);

  const recreateFromDraft = useCallback(async (draftData) => {
    const resolvedStayId = draftData?.stayId || draftData?.stay?.id || draftData?.stay?.slug || '';
    if (!resolvedStayId || !draftData?.checkIn || !draftData?.checkOut) {
      throw new Error('Booking details are missing.');
    }
    const created = await createDraftBooking({
      bookingId: draftData.id || bookingIdParam || effectiveBookingId || '',
      stayId: resolvedStayId,
      stayName: draftData.stayName,
      location: draftData.location,
      checkIn: draftData.checkIn,
      checkOut: draftData.checkOut,
      guests: Number(draftData.guests || 1),
      mealPlanId: draftData.mealPlanId || undefined,
      cardOfferId: draftData.cardOfferId || '',
    }, accessToken);
    let working = created;
    const addonIds = (draftData.addons || [])
      .filter((addon) => addon?.kind !== 'meal_plan' && !String(addon?.id || '').startsWith('meal-plan:'))
      .map((addon) => addon.id)
      .filter(Boolean);
    if (addonIds.length) {
      working = await addBookingAddons(working.id, { addonIds }, accessToken);
    }
    if (draftData.cardOfferId) {
      working = await applyBookingCardOffer(working.id, { cardOfferId: draftData.cardOfferId }, accessToken);
    }
    const code = draftData.coupon?.code || '';
    if (code) {
      working = await applyBookingCoupon(working.id, { code }, accessToken);
    }
    const guest = draftData.guestDetails || {};
    if (guest.fullName && guest.email && guest.phone) {
      working = await saveGuestDetails(working.id, guest, accessToken);
    }
    if (working?.id && guest.fullName && guest.email && guest.phone) {
      working = await confirmBooking(working.id, accessToken, {
        paymentOptionSelected: draftData.paymentOptionSelected || 'full',
      });
    }
    return working;
  }, [accessToken, bookingIdParam, effectiveBookingId]);

  useEffect(() => {
    if (!effectiveBookingId) return;
    let active = true;
    const load = async () => {
      if (!fallbackDraft) {
        setLoading(true);
      }
      setPageError('');
      try {
        const res = await getBookingById(effectiveBookingId);
        if (!active) return;
        const normalized = normalizeBookingDraft(res);
        setBooking(normalized);
        setDraft(normalized);
        if (normalized?.id) setBookingId(normalized.id);
      } catch (err) {
        if (!active) return;
        const isNotFound = err?.status === 404 || /not found/i.test(String(err?.message || ''));
        if (isNotFound && fallbackDraft && !rehydrateAttemptRef.current) {
          rehydrateAttemptRef.current = true;
          try {
            const rebuilt = await recreateFromDraft(fallbackDraft);
            if (!active) return;
            const normalized = normalizeBookingDraft(rebuilt);
            if (normalized) {
              setBooking(normalized);
              setDraft(normalized);
              if (normalized?.id) setBookingId(normalized.id);
              if (bookingIdParam && normalized?.id && String(bookingIdParam) !== String(normalized.id)) {
                navigate('/booking/payment', { replace: true });
              }
              return;
            }
          } catch (rehydrateError) {
            setPageError(rehydrateError.message || 'Failed to rebuild booking.');
            return;
          }
        }
        if (!fallbackDraft) {
          setPageError(err.message || 'Failed to load booking');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [effectiveBookingId, fallbackDraft, bookingIdParam, navigate, recreateFromDraft, setBookingId, setDraft]);

  useEffect(() => {
    if (!booking) return;
    const status = String(booking.status || '').toUpperCase();
    const hasRemainingBalance = Number(booking.remainingAmount || 0) > 0.009;
    if (status === 'PAID' || (status === 'PARTIALLY_PAID' && !hasRemainingBalance)) {
      const targetId = booking.id || effectiveBookingId;
      if (targetId) {
        navigateToInvoice(targetId).then((opened) => {
          if (!opened) navigate(`/booking/success/${targetId}`);
        });
      }
      return;
    }
    requireConfirmed(booking, navigate);
  }, [booking, navigate, effectiveBookingId, navigateToInvoice]);

  useEffect(() => {
    if (!billingSameAsGuest) return;
    if (!guestDetails) return;
    setBillingFields((prev) => ({
      ...prev,
      name: guestDetails.fullName || '',
      email: guestDetails.email || '',
      phone: guestDetails.phone || '',
    }));
  }, [billingSameAsGuest, guestDetails]);

  const pricing = useMemo(() => {
    if (!booking?.pricing) return null;
    const normalized = normalizeBookingPricing(booking.pricing, {
      nights: booking?.nights || 0,
      currency: booking?.pricing?.currency || booking?.currency || stayData?.currency || 'INR',
    });
    return {
      ...booking.pricing,
      base: normalized.basePrice,
      basePrice: normalized.basePrice,
      taxableSubtotal: normalized.taxableSubtotal,
      fees: normalized.mandatoryFees,
      mandatoryFees: normalized.mandatoryFees,
      tax: normalized.gstAmount,
      gstAmount: normalized.gstAmount,
      discount: normalized.discountAmount,
      discountAmount: normalized.discountAmount,
      total: normalized.finalPayable,
      finalPayable: normalized.finalPayable,
      currency: normalized.currency,
      breakdownItems: normalized.breakdownItems,
    };
  }, [booking?.currency, booking?.nights, booking?.pricing, stayData?.currency]);
  const pricingCurrency = pricing?.currency || booking?.currency || stayData?.currency || 'INR';
  const nightsLabel = booking?.nights || 0;
  const taxFeeBreakdown = booking?.taxFeeBreakdown || null;
  const lineItems = useMemo(() => {
    if (!pricing) return [];
    if (Array.isArray(pricing.breakdownItems) && pricing.breakdownItems.length) {
      return pricing.breakdownItems.map((item) => ({
        label: item.label,
        value: item.amount,
      }));
    }
    const rows = [
      {
        label: 'Base Price',
        value: pricing.basePrice,
      },
    ];
    if (pricing.discount) rows.push({ label: 'Discount', value: -Math.abs(pricing.discount) });
    rows.push({ label: 'Taxable Subtotal', value: pricing.taxableSubtotal });
    const taxFeeItems = getTaxFeeLineItems(taxFeeBreakdown);
    if (taxFeeItems.length) {
      rows.push(...taxFeeItems);
    } else {
      if (pricing.fees) rows.push({ label: 'Mandatory fees', value: pricing.fees });
      if (pricing.tax) rows.push({ label: 'GST', value: pricing.tax });
    }
    rows.push({ label: 'Final payable amount', value: pricing.finalPayable });
    return rows;
  }, [pricing, taxFeeBreakdown]);

  const total = pricing?.total || 0;
  const paymentOptions = booking?.paymentOptions || null;
  const partialPaymentOption = paymentOptions?.partial || null;
  const isPartialPaymentAvailable = Boolean(paymentOptions?.partial?.isAvailable);
  const requestedPaymentMode = String(booking?.paymentOptionSelected || paymentOptionSelected || 'full').toLowerCase() === 'partial'
    ? 'partial'
    : 'full';
  const isBalancePaymentDue =
    String(booking?.status || '').toUpperCase() === 'PARTIALLY_PAID' &&
    Number(booking?.remainingAmount || 0) > 0.009;
  const effectivePaymentMode = isBalancePaymentDue
    ? 'balance'
    : requestedPaymentMode === 'partial' && isPartialPaymentAvailable
    ? 'partial'
    : 'full';
  const isPartialPaymentSelected = effectivePaymentMode === 'partial';
  const selectedPaymentPreview = isPartialPaymentSelected
    ? paymentOptions?.partial || null
    : paymentOptions?.full || null;
  const payableNow = Number(
    isBalancePaymentDue
      ? booking?.remainingAmount ?? 0
      : isPartialPaymentSelected
      ? selectedPaymentPreview?.payableNow ?? booking?.amountPaidNow ?? total
      : total
  );
  const remainingAmount = Number(
    isBalancePaymentDue
      ? 0
      : isPartialPaymentSelected
      ? selectedPaymentPreview?.remainingLater ?? booking?.remainingAmount ?? 0
      : 0
  );
  const partialPaymentPercentage = Number(
    partialPaymentOption?.percentage ??
      booking?.partialPaymentPercentage ??
      0
  );
  const stayTitle = booking?.stayName || stayData?.title || stayData?.name || 'Homevilas stay';
  const stayLocation =
    booking?.location ||
    [stayData?.city, stayData?.area].filter(Boolean).join(', ') ||
    stayData?.location ||
    'Location coming soon';
  const stayImage =
    stayData?.coverImage ||
    stayData?.featured_image ||
    stayData?.image_url ||
    'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1200&q=80';
  const stayDateRange = formatDateRange(booking?.checkIn || checkIn, booking?.checkOut || checkOut);
  const stayGuestsLabel = formatGuests(booking?.guests || guests);
  const stayNightsLabel = `${nightsLabel || 0} night${nightsLabel === 1 ? '' : 's'}`;
  const bookingMeta = [stayDateRange, stayNightsLabel, stayGuestsLabel].filter(Boolean);
  const isProcessing = paymentOptionUpdating || razorpayLoading;
  const isPayDisabled = isProcessing;

  const syncBookingState = useCallback((normalized) => {
    if (!normalized) return;
    setBooking(normalized);
    setDraft(normalized);
    if (normalized?.id) setBookingId(normalized.id);
    if (normalized?.id && setStoreBookingId) setStoreBookingId(normalized.id);
    if (setPaymentOptionSelected && normalized?.paymentOptionSelected) {
      setPaymentOptionSelected(normalized.paymentOptionSelected);
    }
  }, [setBookingId, setDraft, setPaymentOptionSelected, setStoreBookingId]);

  const ensureBookingReady = useCallback(async () => {
    if (booking?.id && booking?.status) return booking;
    if (effectiveBookingId) {
      try {
        const res = await getBookingById(effectiveBookingId);
        const normalized = normalizeBookingDraft(res);
        if (normalized) {
          syncBookingState(normalized);
          return normalized;
        }
      } catch (err) {
        const isNotFound = err?.status === 404 || /not found/i.test(String(err?.message || ''));
        if (!isNotFound) throw err;
      }
    }
    if (fallbackDraft) {
      const rebuilt = await recreateFromDraft(fallbackDraft);
      const normalized = normalizeBookingDraft(rebuilt);
      if (normalized) {
        syncBookingState(normalized);
        return normalized;
      }
    }
    throw new Error('Booking not found');
  }, [booking, effectiveBookingId, fallbackDraft, recreateFromDraft, syncBookingState]);

  const handlePaymentOptionChange = useCallback(async (nextOption) => {
    const normalizedOption = nextOption === 'partial' ? 'partial' : 'full';
    if (normalizedOption === effectivePaymentMode) return;
    if (normalizedOption === 'partial' && !isPartialPaymentAvailable) return;
    setPaymentOptionUpdating(true);
    setActionError('');
    try {
      const readyBooking = await ensureBookingReady();
      const updated = await confirmBooking(readyBooking.id || effectiveBookingId, accessToken, {
        paymentOptionSelected: normalizedOption,
      });
      const normalized = normalizeBookingDraft(updated);
      if (normalized) {
        syncBookingState(normalized);
      }
    } catch (err) {
      setActionError(err.message || 'Unable to update payment option.');
    } finally {
      setPaymentOptionUpdating(false);
    }
  }, [
    accessToken,
    effectiveBookingId,
    effectivePaymentMode,
    ensureBookingReady,
    isPartialPaymentAvailable,
    syncBookingState,
  ]);

  const clearFieldError = useCallback((field) => {
    setFormErrors((prev) => {
      if (!prev?.[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const updateBillingField = useCallback((field, value) => {
    setBillingFields((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
  }, [clearFieldError]);

  const handleTermsChange = useCallback((event) => {
    setTermsAccepted(event.target.checked);
    clearFieldError('terms');
  }, [clearFieldError]);

  const validateBeforePay = useCallback(() => {
    const errors = {};
    if (!termsAccepted) {
      errors.terms = 'Please accept the terms to continue.';
    }
    if (!billingSameAsGuest) {
      if (!billingFields.name.trim()) errors.billingName = 'Billing name is required.';
      if (!billingFields.email.trim()) errors.billingEmail = 'Billing email is required.';
      if (!billingFields.phone.trim()) errors.billingPhone = 'Billing phone is required.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [
    billingFields.email,
    billingFields.name,
    billingFields.phone,
    billingSameAsGuest,
    termsAccepted,
  ]);

  const handleRazorpay = async (preferredMethod) => {
    setActionError('');
    setRazorpayLoading(true);
    try {
      const readyBooking = await ensureBookingReady();
      const bookingId = readyBooking.id || effectiveBookingId;
      if (!bookingId) throw new Error('Booking not found');
      const paymentCustomer = billingSameAsGuest
        ? {
            name: guestDetails?.fullName || readyBooking?.guestDetails?.fullName || '',
            email: guestDetails?.email || readyBooking?.guestDetails?.email || '',
            phone: guestDetails?.phone || readyBooking?.guestDetails?.phone || '',
          }
        : {
            name: billingFields.name,
            email: billingFields.email,
            phone: billingFields.phone,
          };
      const order = await createRazorpayOrder(
        {
          bookingId,
          amount: Number(payableNow || 0),
          customerName: paymentCustomer.name,
          customerEmail: paymentCustomer.email,
          customerPhone: paymentCustomer.phone,
          notes: {
            bookingSource: readyBooking?.source || 'checkout',
            selectedMethod: preferredMethod || selectedMethod,
            paymentMode: effectivePaymentMode,
            partialPayment: isPartialPaymentSelected,
            balancePayment: isBalancePaymentDue,
          },
        },
        accessToken
      );
      const ready = await loadRazorpayCheckout();
      if (!ready || !window.Razorpay) {
        throw new Error('Unable to load Razorpay checkout.');
      }
      const billingPrefill = billingSameAsGuest
        ? {}
        : {
            name: billingFields.name,
            email: billingFields.email,
            contact: billingFields.phone,
          };
      const mergedPrefill = {
        name: paymentCustomer.name || billingPrefill.name || '',
        email: paymentCustomer.email || billingPrefill.email || '',
        contact: paymentCustomer.phone || billingPrefill.contact || '',
      };
      let cancellationRecorded = false;
      const recordRazorpayCancellation = async (reason = 'checkout_cancelled') => {
        if (cancellationRecorded) return;
        cancellationRecorded = true;
        try {
          await cancelRazorpayPayment(
            {
              bookingId,
              paymentId: order.paymentId || null,
              orderId: order.orderId,
              reason,
            },
            accessToken
          );
        } catch (err) {
          console.warn('Unable to record Razorpay checkout cancellation', err);
        }
      };
      const options = {
        key: order.key || order.keyId,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'Homevilas',
        description: 'Villa Booking Payment',
        order_id: order.orderId,
        method: 'upi',
        prefill: mergedPrefill,
        remember_customer: false,
        retry: {
          enabled: true,
          max_count: 4,
        },
        theme: {
          color: '#0F5C5A',
        },
        notes: {
          bookingId: String(bookingId),
        },
        config: RAZORPAY_CHECKOUT_CONFIG,
        handler: async (response) => {
          try {
            const verified = await verifyRazorpayPayment(
              {
                bookingId,
                paymentId: order.paymentId || null,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              },
              accessToken
            );
            if (!verified?.success && verified?.verified) {
              setActionError(verified.message || 'Payment authorized and awaiting capture confirmation.');
              setRazorpayLoading(false);
              return;
            }
            const invoiceOpened = await navigateToInvoice(bookingId);
            if (!invoiceOpened) navigate(`/booking/success/${bookingId}`);
          } catch (err) {
            setActionError(err.message || 'Payment verification failed.');
          } finally {
            setRazorpayLoading(false);
          }
        },
        modal: {
          ondismiss: async () => {
            setActionError(PAYMENT_CANCELLED_MESSAGE);
            try {
              await recordRazorpayCancellation('checkout_cancelled');
            } finally {
              setRazorpayLoading(false);
            }
          },
        },
      };
      const instance = new window.Razorpay(options);
      instance.on('payment.failed', async (resp) => {
        const gatewayMessage = resp?.error?.description || 'Payment failed.';
        setActionError(`${gatewayMessage} Please retry with another card, UPI, netbanking, or wallet from Razorpay checkout.`);
        try {
          await recordRazorpayCancellation(resp?.error?.reason || 'payment_failed');
        } finally {
          setRazorpayLoading(false);
        }
      });
      instance.open();
    } catch (err) {
      setRazorpayLoading(false);
      setActionError(String(err?.message || 'Unable to start payment.'));
    }
  };

  const handlePay = async () => {
    if (isProcessing) return;
    setActionError('');
    const valid = validateBeforePay();
    if (!valid) return;
    await handleRazorpay();
  };

  const handleBack = () => navigate('/booking/review');
  const payLabel = isProcessing ? 'Processing payment...' : `Pay ${formatInr(payableNow, pricingCurrency)} & Confirm`;
  const supportLine = 'Call +918287703118 or email info@homevilas.com, meena@homevilas.com';
  const isPaymentCancelled = actionError === PAYMENT_CANCELLED_MESSAGE;
  const inputClass = styles.textInput;
  const selectedMethodMeta = PAYMENT_METHODS.find((method) => method.id === selectedMethod) || PAYMENT_METHODS[0];
  const SelectedMethodIcon = selectedMethodMeta.icon;
  const selectedMethodProviderHint = PAYMENT_PROVIDER_HINTS[selectedMethodMeta.id] || 'Powered by Homevilas';
  const selectedMethodGatewayNote = 'This payment continues through Razorpay secure checkout.';
  const selectedMethodGroupId = PAYMENT_METHOD_TO_GROUP[selectedMethod] || PAYMENT_METHOD_GROUPS[0].id;
  const selectedMethodGroup = PAYMENT_METHOD_GROUPS.find((group) => group.id === selectedMethodGroupId) || PAYMENT_METHOD_GROUPS[0];
  const handleMethodGroupSelect = useCallback((groupId) => {
    const nextGroup = PAYMENT_METHOD_GROUPS.find((group) => group.id === groupId);
    if (!nextGroup) return;
    if (!nextGroup.methods.includes(selectedMethod)) {
      setSelectedMethod(nextGroup.methods[0]);
    }
  }, [selectedMethod]);

  if (!effectiveBookingId) {
    return (
      <div className="min-h-screen bg-[#F8F6F2] px-4 py-10">
        <div className="mx-auto max-w-4xl rounded-3xl border border-dashed border-[#E6DED1] bg-white p-10 text-center shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
          <h2 className="text-2xl font-semibold text-[#10233D]">Payment needs a booking</h2>
          <p className="mt-2 text-sm text-[#667085]">Return to guest details to complete the booking.</p>
          <button
            type="button"
            onClick={() => navigate('/booking/guest')}
            className="mt-6 rounded-full bg-[#0F5C5A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#0A4443]"
          >
            Back to guest details
          </button>
        </div>
      </div>
    );
  }

  if (loading && !booking) return <div className="p-4 text-[#667085]">Loading payment...</div>;
  if (!booking && pageError) return <div className="p-4 text-red-600">{pageError}</div>;
  if (!booking) return null;

  return (
    <>
    <div className="hv-mobile-only hv-ref-booking-screen hv-ref-payment-mobile">
      <main className="hv-ref-booking-main">
        <section className="hv-ref-booking-card">
          <div className="hv-ref-eyebrow">Complete your payment</div>
          <h1>{booking.id ? `#${booking.id}` : 'Draft booking'}</h1>
          <div className="hv-ref-payment-meta">
            <span><CalendarIcon className="h-4 w-4" /> {stayDateRange}</span>
            <span>{stayNightsLabel}</span>
            <span><UsersIcon className="h-4 w-4" /> {stayGuestsLabel}</span>
          </div>
        </section>

        {isPaymentCancelled ? (
          <section className="hv-ref-booking-card hv-ref-cancel-card" role="status" aria-live="polite">
            <div className="hv-ref-cancel-icon">!</div>
            <div>
              <div className="hv-ref-eyebrow">Payment paused</div>
              <h2>No amount was charged</h2>
              <p>Razorpay checkout was closed before completion. Your booking is saved, and you can retry securely whenever ready.</p>
              <div className="hv-ref-cancel-actions">
                <button type="button" onClick={handlePay} disabled={isPayDisabled}>Retry Razorpay</button>
                <button type="button" onClick={handleBack}>Back to review</button>
              </div>
            </div>
          </section>
        ) : actionError ? (
          <p className="hv-flow-error" role="alert">{actionError}</p>
        ) : null}

        <section className="hv-ref-booking-card">
          <div className="hv-ref-eyebrow">Choose payment option</div>
          <div className="hv-ref-pay-options">
            <button
              type="button"
              className={effectivePaymentMode === 'full' ? 'selected' : ''}
              onClick={() => handlePaymentOptionChange('full')}
              disabled={paymentOptionUpdating}
            >
              <span>Pay full amount</span>
              <strong>{formatInr(total, pricingCurrency)}</strong>
              <small>Pay 100% now to confirm the stay in one step.</small>
            </button>
            {isPartialPaymentAvailable ? (
              <button
                type="button"
                className={isPartialPaymentSelected ? 'selected' : ''}
                onClick={() => handlePaymentOptionChange('partial')}
                disabled={paymentOptionUpdating}
              >
                <span>Pay {partialPaymentPercentage}% deposit</span>
                <strong>{formatInr(paymentOptions?.partial?.payableNow || 0, pricingCurrency)}</strong>
                <small>Balance is collected as per property payment policy.</small>
              </button>
            ) : null}
          </div>
        </section>

        <section className="hv-ref-booking-card">
          <div className="hv-ref-eyebrow">Billing information</div>
          <div className="hv-ref-billing-rows">
            <div><span>Name</span><strong>{billingFields.name || booking?.guestDetails?.fullName || '-'}</strong></div>
            <div><span>Email</span><strong>{billingFields.email || booking?.guestDetails?.email || '-'}</strong></div>
            <div><span>Phone</span><strong>{billingFields.phone || booking?.guestDetails?.phone || '-'}</strong></div>
          </div>
          <label className="hv-ref-terms">
            <input type="checkbox" checked={termsAccepted} onChange={handleTermsChange} />
            <span>I agree to the <Link to="/terms">Terms of Service</Link>, cancellation policy rules, and privacy codes of Homevilas.</span>
          </label>
          {formErrors.terms ? <p className="hv-flow-error">{formErrors.terms}</p> : null}
          <button className="hv-ref-pay-final" type="button" onClick={handlePay} disabled={isPayDisabled}>
            {isProcessing ? 'Processing…' : `Pay ${formatInr(payableNow, pricingCurrency)} & confirm →`}
          </button>
        </section>

        <section className="hv-ref-booking-card">
          <div className="hv-ref-eyebrow">Payment summary</div>
          <h2>{stayTitle}</h2>
          <p className="hv-ref-sub">{stayLocation}</p>
          <div className="hv-ref-price-card">
            {lineItems.map((row) => (
              <div key={row.label}><span>{row.label}</span><b>{formatInr(row.value, pricingCurrency)}</b></div>
            ))}
            <div className="total"><span>Amount to pay now</span><b>{formatInr(payableNow, pricingCurrency)}</b></div>
          </div>
        </section>
      </main>
      <div className="hv-ref-bar">
        <button className="hv-ref-total" type="button"><span>Grand total</span><strong>{formatInr(payableNow, pricingCurrency)}</strong></button>
        <button className="hv-ref-book" type="button" onClick={handlePay} disabled={isPayDisabled}>Razorpay →</button>
      </div>
    </div>
    <div className={`hv-desktop-flow relative min-h-screen pb-24 text-[#243444] ${styles.pageShell}`}>
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-0 h-80 w-80 rounded-full bg-[#F4E7CF] opacity-70 blur-3xl" />
        <div className="absolute left-0 top-24 h-72 w-72 rounded-full bg-[#DDF1EE] opacity-70 blur-3xl" />
      </div>
      <div className={`relative mx-auto flex max-w-7xl flex-col gap-8 px-4 py-10 sm:px-6 xl:px-8 ${styles.pageContainer}`}>
        <section className={`animate-fade-up ${styles.heroCard}`}>
          <div className={styles.heroTopRow}>
            <div className={styles.heroIntro}>
              <nav className={styles.heroBreadcrumb}>
                <Link to="/" className="hover:text-[#10233D]">
                  Home
                </Link>
                <span className="text-slate-300">/</span>
                <Link to="/listings" className="hover:text-[#10233D]">
                  Stays
                </Link>
                <span className="text-slate-300">/</span>
                <span className={styles.breadcrumbCurrent}>Payment</span>
              </nav>
              <div>
                <p className={styles.heroEyebrow}>Complete your payment</p>
                <h1 className={styles.heroTitle}>{booking.id ? `#${booking.id}` : 'Draft booking'}</h1>
              </div>
            </div>

            <div className={styles.heroAside}>
              <div className={styles.progressRail}>
                {['Search', 'Details', 'Review', 'Payment'].map((step) => (
                  <span
                    key={step}
                    className={`${styles.progressStep} ${step === 'Payment' ? styles.progressStepActive : ''}`}
                  >
                    {step}
                  </span>
                ))}
              </div>

              {bookingMeta.length ? (
                <div className={styles.heroMeta}>
                  {stayDateRange ? (
                    <span className={styles.heroMetaItem}>
                      <CalendarIcon className="h-4 w-4 text-[#8B97A7]" />
                      {stayDateRange}
                    </span>
                  ) : null}
                  {stayNightsLabel ? <span className={styles.heroMetaItem}>{stayNightsLabel}</span> : null}
                  {stayGuestsLabel ? (
                    <span className={styles.heroMetaItem}>
                      <UsersIcon className="h-4 w-4 text-[#8B97A7]" />
                      {stayGuestsLabel}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {pageError && <div className={`${styles.statusBanner} ${styles.statusBannerWarning}`}>{pageError}</div>}
        {isPaymentCancelled ? (
          <PaymentCancelledPanel
            bookingId={booking?.id || effectiveBookingId}
            payableNow={payableNow}
            total={total}
            remainingAmount={remainingAmount}
            currency={pricingCurrency}
            isPartial={isPartialPaymentSelected}
            paymentMode={isPartialPaymentSelected ? `${partialPaymentPercentage}% now` : ''}
            onRetry={handlePay}
            onReview={handleBack}
            disabled={isPayDisabled}
            supportLine={supportLine}
          />
        ) : actionError ? (
          <div
            className={`${styles.statusBanner} ${
              actionError === PAYMENT_CANCELLED_MESSAGE ? styles.statusBannerWarning : styles.statusBannerError
            }`}
          >
            {actionError}
          </div>
        ) : null}

        {/* UI-only reorganization: existing payment logic, validation, and API flow remain unchanged. */}
        <div className={styles.contentGrid}>
          <main className={`animate-fade-up-delay-1 ${styles.mainColumn}`}>
            <section className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTitleGroup}>
                  <p className={styles.sectionLabel}>Payment stage</p>
                  <h2 className={styles.sectionTitle}>Payment method</h2>
                  <p className={styles.sectionHelp}>Choose how you would like to pay and confirm your stay securely.</p>
                </div>
                <span className={styles.secureBadge}>
                  <LockIcon className="h-4 w-4" />
                  Secure checkout
                </span>
              </div>
              {isPartialPaymentAvailable ? (
                <div className={styles.choicePanel}>
                  <div className={styles.choiceHeader}>
                    <div>
                      <p className={styles.sectionLabel}>Payment choice</p>
                      <p className={styles.sectionHelp}>
                        Choose whether to pay the full booking amount or the eligible advance now.
                      </p>
                    </div>
                    {paymentOptionUpdating ? <span className="text-xs font-semibold text-[#667085]">Updating...</span> : null}
                  </div>
                  <div className={styles.choiceGrid}>
                    <button
                      type="button"
                      onClick={() => handlePaymentOptionChange('full')}
                      disabled={paymentOptionUpdating}
                      className={`${styles.choiceCard} ${
                        effectivePaymentMode === 'full' ? styles.choiceCardFullActive : ''
                      } disabled:cursor-not-allowed disabled:opacity-70`}
                    >
                      <div className={styles.choiceCardTop}>
                        <div className={styles.choiceText}>
                          <span className={styles.choiceLabel}>Pay Full Amount</span>
                          <span className={styles.choiceDescription}>The full booking amount will be charged now.</span>
                        </div>
                        <span className={styles.choiceAmount}>{formatInr(total, pricingCurrency)}</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePaymentOptionChange('partial')}
                      disabled={paymentOptionUpdating}
                      className={`${styles.choiceCard} ${
                        effectivePaymentMode === 'partial' ? styles.choiceCardPartialActive : ''
                      } disabled:cursor-not-allowed disabled:opacity-70`}
                    >
                      <div className={styles.choiceCardTop}>
                        <div className={styles.choiceText}>
                          <span className={styles.choiceLabel}>Pay Partial Amount</span>
                          <span className={styles.choiceDescription}>
                            {partialPaymentPercentage}% now, with the balance marked as pay at check-in.
                          </span>
                        </div>
                        <span className={`${styles.choiceAmount} ${styles.choiceAmountPartial}`}>
                          {formatInr(paymentOptions?.partial?.payableNow || 0, pricingCurrency)}
                        </span>
                      </div>
                    </button>
                  </div>

                  {isPartialPaymentSelected ? (
                    <div className={styles.breakdownCard}>
                      <div className={styles.breakdownRow}>
                        <span>Partial percentage</span>
                        <span className="font-semibold">{partialPaymentPercentage}%</span>
                      </div>
                      <div className={styles.breakdownRow}>
                        <span>Pay now</span>
                        <span className="font-semibold">
                          {formatInr(paymentOptions?.partial?.payableNow || 0, pricingCurrency)}
                        </span>
                      </div>
                      <div className={styles.breakdownRow}>
                        <span>Pay at check-in</span>
                        <span className="font-semibold">
                          {formatInr(paymentOptions?.partial?.remainingLater || 0, pricingCurrency)}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className={styles.methodSectionIntro}>
                <p className={styles.sectionLabel}>Payment options</p>
                <p className={styles.sectionHelp}>
                  Razorpay is the only enabled final payment gateway. UPI, cards, wallets, and netbanking are available inside Razorpay checkout.
                </p>
              </div>

              <div className={styles.methodGrid}>
                <div className={styles.methodSelector}>
                  <div className={styles.methodTabs}>
                    {PAYMENT_METHOD_GROUPS.map((group) => (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => handleMethodGroupSelect(group.id)}
                        className={`${styles.methodTab} ${
                          group.id === selectedMethodGroupId ? styles.methodTabActive : ''
                        }`}
                      >
                        {group.tabLabel}
                      </button>
                    ))}
                  </div>

                  <div className={styles.methodPanel}>
                    <div className={styles.methodPanelHeader}>
                      <div>
                        <p className={styles.sectionLabel}>Payment gateway</p>
                        <h3 className={styles.methodPanelTitle}>{selectedMethodGroup.title}</h3>
                        <p className={styles.sectionHelp}>{selectedMethodGroup.description}</p>
                      </div>
                      {selectedMethodGroup.badge ? (
                        <span className={styles.methodPanelBadge}>{selectedMethodGroup.badge}</span>
                      ) : null}
                    </div>

                    <div
                      className={`${styles.methodOptionTabs} ${
                        selectedMethodGroup.methods.length === 1 ? styles.methodOptionGridSingle : ''
                      }`}
                    >
                      {selectedMethodGroup.methods.map((methodId) => {
                        const method = PAYMENT_METHODS.find((item) => item.id === methodId);
                        if (!method) return null;
                        return (
                          <PaymentMethodTab
                            key={method.id}
                            method={method}
                            isActive={method.id === selectedMethod}
                            onSelect={() => setSelectedMethod(method.id)}
                          />
                        );
                      })}
                    </div>

                    <div className={styles.methodSelectionNote}>
                      <span
                        className={`${styles.methodIcon} ${styles.methodSelectionIcon} ${
                          selectedMethodMeta.highlight ? styles.methodIconHighlight : ''
                        } ${styles.methodIconActive}`}
                      >
                        <SelectedMethodIcon className="h-4 w-4" />
                      </span>
                      <div className={styles.methodSelectionCopy}>
                        <p className={styles.methodSelectionTitle}>{selectedMethodMeta.label}</p>
                        <p className={styles.methodSelectionDescription}>{selectedMethodGatewayNote}</p>
                        <p className={styles.methodSelectionProvider}>{selectedMethodProviderHint}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.detailsColumn}>
                  <PaymentDetailsPanel
                    methodId={selectedMethod}
                    isPartialPaymentSelected={isPartialPaymentSelected}
                    partialPaymentPercentage={partialPaymentPercentage}
                    payableNow={payableNow}
                    remainingAmount={remainingAmount}
                    pricingCurrency={pricingCurrency}
                  />
                </div>
              </div>

              <PaymentActionBar
                payLabel={payLabel}
                onPay={handlePay}
                onBack={handleBack}
                disabled={isPayDisabled}
                termsAccepted={termsAccepted}
                onTermsChange={handleTermsChange}
                termsError={formErrors.terms}
              />
            </section>

            <section className={styles.sectionCard}>
              <div className={styles.billingHeader}>
                <div className={styles.sectionTitleGroup}>
                  <p className={styles.sectionLabel}>Invoice information</p>
                  <h2 className={styles.sectionTitle}>Billing details</h2>
                  <p className={styles.sectionHelp}>Used for invoice and payment confirmation.</p>
                </div>
                <label className={styles.billingToggle}>
                  <input
                    type="checkbox"
                    checked={billingSameAsGuest}
                    onChange={(event) => {
                      setBillingSameAsGuest(event.target.checked);
                      clearFieldError('billingName');
                      clearFieldError('billingEmail');
                      clearFieldError('billingPhone');
                    }}
                    className={`${styles.checkControl} h-4 w-4 rounded border-[#DDD5C7] text-[#0F5C5A] focus:ring-[#DDF1EE]`}
                  />
                  Same as guest details
                </label>
              </div>
              <div className={styles.billingGrid}>
                <div className={styles.formField}>
                  <label htmlFor="billing-name" className={styles.fieldLabel}>
                    Full name
                  </label>
                  <input
                    id="billing-name"
                    type="text"
                    value={billingFields.name}
                    onChange={(event) => updateBillingField('name', event.target.value)}
                    disabled={billingSameAsGuest}
                    aria-invalid={Boolean(formErrors.billingName)}
                    className={`${inputClass} ${billingSameAsGuest ? styles.textInputDisabled : ''}`}
                  />
                  {formErrors.billingName && <p className={styles.fieldError}>{formErrors.billingName}</p>}
                </div>
                <div className={styles.formField}>
                  <label htmlFor="billing-email" className={styles.fieldLabel}>
                    Email
                  </label>
                  <input
                    id="billing-email"
                    type="email"
                    value={billingFields.email}
                    onChange={(event) => updateBillingField('email', event.target.value)}
                    disabled={billingSameAsGuest}
                    aria-invalid={Boolean(formErrors.billingEmail)}
                    className={`${inputClass} ${billingSameAsGuest ? styles.textInputDisabled : ''}`}
                  />
                  {formErrors.billingEmail && <p className={styles.fieldError}>{formErrors.billingEmail}</p>}
                </div>
                <div className={styles.formField}>
                  <label htmlFor="billing-phone" className={styles.fieldLabel}>
                    Phone
                  </label>
                  <input
                    id="billing-phone"
                    type="tel"
                    value={billingFields.phone}
                    onChange={(event) => updateBillingField('phone', event.target.value)}
                    disabled={billingSameAsGuest}
                    aria-invalid={Boolean(formErrors.billingPhone)}
                    className={`${inputClass} ${billingSameAsGuest ? styles.textInputDisabled : ''}`}
                  />
                  {formErrors.billingPhone && <p className={styles.fieldError}>{formErrors.billingPhone}</p>}
                </div>
                <div className={styles.formField}>
                  <label htmlFor="billing-address" className={styles.fieldLabel}>
                    Address (optional)
                  </label>
                  <input
                    id="billing-address"
                    type="text"
                    value={billingFields.address}
                    onChange={(event) => updateBillingField('address', event.target.value)}
                    disabled={billingSameAsGuest}
                    className={`${inputClass} ${billingSameAsGuest ? styles.textInputDisabled : ''}`}
                  />
                </div>
              </div>
            </section>
          </main>

          <aside className={`animate-fade-up-delay-2 ${styles.sidebarColumn}`}>
            <section className={styles.summaryCard}>
              <div className={styles.summaryTop}>
                <div className={styles.summaryThumb}>
                  <img src={stayImage} alt={stayTitle} className="h-full w-full object-cover" />
                </div>
                <div className={styles.summaryIntro}>
                  <p className={styles.summaryEyebrow}>Payment summary</p>
                  <h2 className={styles.summaryTitle}>{stayTitle}</h2>
                  <p className={styles.summaryLocation}>
                    <MapPinIcon className="h-4 w-4 text-[#8B97A7]" />
                    {stayLocation}
                  </p>
                </div>
              </div>

              <div className={styles.summaryMeta}>
                {stayDateRange ? (
                  <span className={styles.summaryMetaItem}>
                    <CalendarIcon className="h-4 w-4 text-[#8B97A7]" />
                    {stayDateRange}
                  </span>
                ) : null}
                {stayNightsLabel ? <span className={styles.summaryMetaItem}>{stayNightsLabel}</span> : null}
                {stayGuestsLabel ? (
                  <span className={styles.summaryMetaItem}>
                    <UsersIcon className="h-4 w-4 text-[#8B97A7]" />
                    {stayGuestsLabel}
                  </span>
                ) : null}
              </div>

              <div className={styles.summaryBreakdown}>
                {lineItems.map((row) => (
                  <div key={row.label} className={styles.summaryBreakdownRow}>
                    <span>{row.label}</span>
                    <span className="font-medium text-[#10233D]">{formatInr(row.value, pricingCurrency)}</span>
                  </div>
                ))}
              </div>

              <div className={styles.summaryOptionCard}>
                <div className={styles.summaryOptionHeader}>
                  <span className="font-semibold text-[#10233D]">Payment option</span>
                  <span className={styles.summaryOptionTag}>{isPartialPaymentSelected ? 'Partial' : 'Full'}</span>
                </div>
                {isPartialPaymentSelected ? (
                  <div className="mt-3 space-y-2 text-sm text-[#243444]">
                    <div className={styles.summaryBreakdownRow}>
                      <span>Advance percentage</span>
                      <span className="font-semibold">{partialPaymentPercentage}%</span>
                    </div>
                    <div className={styles.summaryBreakdownRow}>
                      <span>Pay now</span>
                      <span className="font-semibold text-[#10233D]">{formatInr(payableNow, pricingCurrency)}</span>
                    </div>
                    <div className={styles.summaryBreakdownRow}>
                      <span>Pay at check-in</span>
                      <span className="font-semibold text-[#10233D]">{formatInr(remainingAmount, pricingCurrency)}</span>
                    </div>
                  </div>
                ) : (
                  <p className={styles.summaryOptionNote}>The full booking amount will be charged now.</p>
                )}
                <p className={styles.summaryOptionNote}>Checkout method: Razorpay</p>
              </div>

              <div className={styles.summaryTotals}>
                <div className={styles.summaryTotalRow}>
                  <span>Total payable</span>
                  <span>{formatInr(total, pricingCurrency)}</span>
                </div>
                <div className={styles.summaryPayNowRow}>
                  <span>Amount to pay now</span>
                  <span className="font-semibold text-[#10233D]">{formatInr(payableNow, pricingCurrency)}</span>
                </div>
                {isPartialPaymentSelected ? (
                  <div className={styles.summaryPayNowRow}>
                    <span>Pay at check-in</span>
                    <span className="font-semibold text-[#10233D]">{formatInr(remainingAmount, pricingCurrency)}</span>
                  </div>
                ) : null}
              </div>

              <div className={styles.summaryTrustList}>
                <div className={styles.summaryTrustItem}>
                  <LockIcon className="h-4 w-4 text-[#0F5C5A]" />
                  100% secure payments
                </div>
                <div className={styles.summaryTrustItem}>
                  <ShieldCheckIcon className="h-4 w-4 text-[#0F5C5A]" />
                  No hidden charges
                </div>
                <div className={styles.summaryTrustItem}>
                  <CalendarIcon className="h-4 w-4 text-[#0F5C5A]" />
                  Instant confirmation
                </div>
              </div>

              <div className={styles.summarySupport}>
                <strong>Need help? Contact support</strong>
                <div>{supportLine}</div>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
    </>
  );
};

export default PaymentPage;



